import jsPDF from 'jspdf';
import { parseEuroNumber, formatIBANForDisplay, formatDate, formatMoney } from './helper';

const formatAmount = formatMoney;

export const generateInvoicePDF = ({
  invoiceNumber,
  month,
  year,
  commissions = [],
  isBatch = false,
  agentDetails = {},
}) => {
  const doc = new jsPDF();
  const dateToday = formatDate(new Date());
  const baseLineHeight = 6;
  const rightColumnWidth = 80;
  const pageBottom = 285;

  // Determine company status and country
  const countryNormalized = (agentDetails.country || '').trim().toLowerCase();
  const isNetherlands = countryNormalized === 'netherlands' || countryNormalized === 'the netherlands';
  const trimmedCompanyName = agentDetails.companyName ? agentDetails.companyName.trim() : '';
  const hasCompany = !!trimmedCompanyName;
  const shouldChargeVAT = isNetherlands && hasCompany;

  // Determine document title: INVOICE or RECEIPT
  const documentTitle = hasCompany ? 'INVOICE' : 'RECEIPT';

  // Header with logo and title
  doc.setFontSize(24);
  doc.setFont('helvetica', 'bold');
  doc.text(documentTitle, 20, 20);
  doc.setFontSize(18);
  doc.text('TechXpander', 190, 20, { align: 'right' });
  doc.line(20, 24, 190, 24);

  // Left column: Lead Agent details (sender)
  let leftY = 32;
  doc.setFontSize(11);
  doc.setFont('helvetica', 'normal');

  // Full Name
  doc.text(`${agentDetails.firstName || 'First Name'} ${agentDetails.lastName || 'Last Name'}`, 20, leftY);
  leftY += baseLineHeight;

  // Company Name (omit if empty)
  if (hasCompany) {
    doc.text(trimmedCompanyName, 20, leftY);
    leftY += baseLineHeight;
  }

  // Address Line 1
  doc.text(agentDetails.address || 'Address', 20, leftY);
  leftY += baseLineHeight;

  // Address Line 2 (if filled)
  if (agentDetails.addressLine2) {
    doc.text(agentDetails.addressLine2, 20, leftY);
    leftY += baseLineHeight;
  }

  // Postal / ZIP + City (on same line)
  const postalCity = `${agentDetails.zip || 'Postal/ZIP'} ${agentDetails.city || 'City'}`;
  doc.text(postalCity, 20, leftY);
  leftY += baseLineHeight;

  // State (if filled)
  if (agentDetails.state) {
    doc.text(agentDetails.state, 20, leftY);
    leftY += baseLineHeight;
  }

  // Country
  doc.text(agentDetails.country || 'Country', 20, leftY);
  leftY += baseLineHeight;

  // Email
  doc.text(agentDetails.email || 'Email', 20, leftY);
  leftY += baseLineHeight;

  // IBAN (only for Netherlands - placed in left column under Email)
  if (isNetherlands && agentDetails.iban) {
    doc.text(`IBAN / Bank Account Number: ${formatIBANForDisplay(agentDetails.iban)}`, 20, leftY);
    leftY += baseLineHeight;
  }

  // VAT / Tax ID (if filled)
  if (agentDetails.vat) {
    doc.text(`VAT / Tax ID: ${agentDetails.vat}`, 20, leftY);
    leftY += baseLineHeight;
  }

  // Company Registration Number / CoC (if filled)
  if (hasCompany && agentDetails.companyRegistrationNumber) {
    doc.text(`Company Registration Number: ${agentDetails.companyRegistrationNumber}`, 20, leftY);
    leftY += baseLineHeight;
  }

  // Right column: Bank Details (only if Country ≠ Netherlands)
  let rightY = 32;
  if (!isNetherlands) {
    doc.setFont('helvetica', 'bold');
    doc.text('Bank Details:', 120, rightY);
    rightY += baseLineHeight;
    doc.setFont('helvetica', 'normal');

    // Bank Name
    if (agentDetails.bankName) {
      doc.text(agentDetails.bankName, 120, rightY);
      rightY += baseLineHeight;
    }

    // IBAN / Bank Account Number (in right column for non-NL)
    if (agentDetails.iban) {
      doc.text('IBAN / Bank Account Number:', 120, rightY);
      rightY += baseLineHeight;
      const ibanLinesRight = doc.splitTextToSize(formatIBANForDisplay(agentDetails.iban), rightColumnWidth);
      ibanLinesRight.forEach((line) => {
        doc.text(line, 120, rightY);
        rightY += baseLineHeight;
      });
    }

    // SWIFT / Routing Code
    if (agentDetails.bic) {
      doc.text('SWIFT / Routing Code:', 120, rightY);
      rightY += baseLineHeight;
      const bicLinesRight = doc.splitTextToSize(agentDetails.bic, rightColumnWidth);
      bicLinesRight.forEach((line) => {
        doc.text(line, 120, rightY);
        rightY += baseLineHeight;
      });
    }

    // Bank Address Line 1
    if (agentDetails.bankAddressLine1) {
      const bankAddressLine1Lines = doc.splitTextToSize(agentDetails.bankAddressLine1, rightColumnWidth);
      bankAddressLine1Lines.forEach((line) => {
        doc.text(line, 120, rightY);
        rightY += baseLineHeight;
      });
    }

    // Bank Address Line 2 (if filled)
    if (agentDetails.bankAddressLine2) {
      const bankAddressLine2Lines = doc.splitTextToSize(agentDetails.bankAddressLine2, rightColumnWidth);
      bankAddressLine2Lines.forEach((line) => {
        doc.text(line, 120, rightY);
        rightY += baseLineHeight;
      });
    }

    // Bank Postal / City
    const bankPostalCity = [];
    if (agentDetails.bankCity) bankPostalCity.push(agentDetails.bankCity);
    // Note: bank zip not in model, but if added later, include it
    if (bankPostalCity.length > 0) {
      const bankPostalCityLines = doc.splitTextToSize(bankPostalCity.join(', '), rightColumnWidth);
      bankPostalCityLines.forEach((line) => {
        doc.text(line, 120, rightY);
        rightY += baseLineHeight;
      });
    }

    // Bank State (if filled)
    if (agentDetails.bankState) {
      doc.text(agentDetails.bankState, 120, rightY);
      rightY += baseLineHeight;
    }

    // Bank Country
    if (agentDetails.bankCountry) {
      doc.text(agentDetails.bankCountry, 120, rightY);
      rightY += baseLineHeight;
    }
  }

  // Draw separator line after header section
  const headerEndY = Math.max(leftY, rightY) + 4;
  doc.line(20, headerEndY, 190, headerEndY);

  // TechXpander billing info
  let billToY = headerEndY + 8;
  doc.setFont('helvetica', 'bold');
  doc.text('Bill To:', 20, billToY);
  doc.setFont('helvetica', 'normal');
  billToY += 6;
  doc.text('TechXpander trading under Snowcat B.V.', 20, billToY);
  billToY += 6;
  doc.text('Stationsstraat 14b', 20, billToY);
  billToY += 6;
  doc.text('1391 GN Abcoude', 20, billToY);
  billToY += 6;
  doc.text('The Netherlands', 20, billToY);
  billToY += 6;
  doc.text('Billing inquiries: info@techxpander.com | +31 (0)6 19 598 489', 20, billToY);
  billToY += 8;
  doc.line(20, billToY, 190, billToY);

  // Invoice/Receipt details
  let detailsY = billToY + 8;
  doc.text(`${documentTitle === 'INVOICE' ? 'Invoice' : 'Receipt'} Date: ${dateToday}`, 20, detailsY);
  detailsY += 6;
  doc.text(`${documentTitle === 'INVOICE' ? 'Invoice' : 'Receipt'} Number: ${invoiceNumber || ''}`, 20, detailsY);
  detailsY += 6;
  doc.text(`Month: ${month} ${year}`, 20, detailsY);
  detailsY += 6;
  doc.text('Payment Terms: Payment due within 14 days', 20, detailsY);
  detailsY += 6;
  doc.text('Payment Method: Bank Transfer', 20, detailsY);
  detailsY += 8;
  doc.line(20, detailsY, 190, detailsY);

  // Table column definitions
  const columnConfig = {
    client: { x: 20, width: 35 }, // 20 to 55
    prospect: { x: 60, width: 35 }, // 60 to 95
    eventType: { x: 100, width: 45 }, // 100 to 145
    amount: { x: 150, width: 40 }, // 150 to 190
  };
  const lineHeight = baseLineHeight;

  const ensureSpace = (currentY, requiredHeight, { addHeader = false } = {}) => {
    if (currentY + requiredHeight > pageBottom) {
      doc.addPage();
      if (addHeader) {
        return drawTableHeader(20);
      }
      return 20;
    }
    return currentY;
  };

  const drawTableHeader = (startY) => {
    let headerY = startY;
    doc.setFont('helvetica', 'bold');
    doc.text('Client', columnConfig.client.x, headerY);
    doc.text('Prospect', columnConfig.prospect.x, headerY);
    doc.text('Event Type', columnConfig.eventType.x, headerY);
    doc.text('Amount', columnConfig.amount.x, headerY);
    doc.line(20, headerY + 2, 190, headerY + 2);
    headerY += 8;
    doc.setFont('helvetica', 'normal');
    return headerY;
  };

  // Table header
  let y = drawTableHeader(detailsY + 8);

  // Table rows with text wrapping
  let total = 0;
  doc.setFont('helvetica', 'normal');
  commissions.forEach((entry, index) => {
    const intPart = parseEuroNumber(entry.commission);
    const decPart = parseInt(entry.cents || '0', 10);
    const fullAmount = intPart + decPart / 100;
    total += fullAmount;

    // Prepare cell content
    const clientText =
      entry.client?.company?.companyName ||
      [entry.client?.firstName, entry.client?.lastName].filter(Boolean).join(' ') ||
      '';
    const prospectText = entry.prospect || '';
    const eventTypeText = entry.event === 'Other' && entry.otherEvent ? entry.otherEvent : entry.event || '';
    const amountText = formatAmount(fullAmount);

    // Wrap text for each column
    const clientLines = doc.splitTextToSize(clientText, columnConfig.client.width);
    const prospectLines = doc.splitTextToSize(prospectText, columnConfig.prospect.width);
    const eventTypeLines = doc.splitTextToSize(eventTypeText, columnConfig.eventType.width);
    const amountLines = doc.splitTextToSize(amountText, columnConfig.amount.width);

    // Find the maximum number of lines for this row
    const maxLines = Math.max(clientLines.length, prospectLines.length, eventTypeLines.length, amountLines.length, 1);
    const rowHeight = maxLines * lineHeight + 2;

    y = ensureSpace(y, rowHeight, { addHeader: true });

    // Draw each cell with wrapped text
    const rowStartY = y;
    clientLines.forEach((line, indexLine) => {
      doc.text(line, columnConfig.client.x, rowStartY + indexLine * lineHeight);
    });
    prospectLines.forEach((line, indexLine) => {
      doc.text(line, columnConfig.prospect.x, rowStartY + indexLine * lineHeight);
    });
    eventTypeLines.forEach((line, indexLine) => {
      doc.text(line, columnConfig.eventType.x, rowStartY + indexLine * lineHeight);
    });
    amountLines.forEach((line, indexLine) => {
      doc.text(line, columnConfig.amount.x, rowStartY + indexLine * lineHeight);
    });

    // Move Y position based on the tallest cell
    y += rowHeight;
  });

  y += 5;
  const totalsBlockHeight = 10 + 6 * 3;
  y = ensureSpace(y, totalsBlockHeight);

  // Totals aligned under Amount column
  doc.text(formatAmount(total), 160, y, { align: 'right' });
  doc.text('Subtotal', 190, y, { align: 'right' });
  y += 6;
  const vatRate = shouldChargeVAT ? 0.21 : 0;
  const vat = total * vatRate;
  doc.text(formatAmount(vat), 160, y, { align: 'right' });
  doc.text(`${shouldChargeVAT ? 'VAT (21%)' : 'VAT (0%)'}`, 190, y, { align: 'right' });
  y += 6;
  doc.setFont('helvetica', 'bold');
  doc.text(formatAmount(total + vat), 160, y, { align: 'right' });
  doc.text('Total Due', 190, y, { align: 'right' });
  doc.line(140, y + 2, 190, y + 2);
  y += 10;

  // VAT explanatory note based on scenario
  let vatNoteText = '';
  if (hasCompany && isNetherlands) {
    vatNoteText = 'VAT 21% applied under Dutch tax law.';
  } else if (hasCompany && !isNetherlands) {
    vatNoteText = 'VAT not applicable – reverse charge where applicable.';
  } else {
    // Natural person (any country)
    vatNoteText = 'VAT not applicable – supplier is a private individual (non-business).';
  }

  if (vatNoteText) {
    const wrapped = doc.splitTextToSize(vatNoteText, 170);
    const noteHeight = wrapped.length * lineHeight + 6;
    y = ensureSpace(y, noteHeight);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(11);
    doc.text(wrapped, 20, y);
    y += wrapped.length * lineHeight + 6;
  }

  // Optional note
  const thanksHeight = lineHeight + 4;
  y = ensureSpace(y, thanksHeight);
  doc.setFont('helvetica', 'italic');
  doc.text('Thank you for your partnership.', 20, y);

  const filename = isBatch
    ? `Batch_${documentTitle}s_${year}_${month}.pdf`
    : `${documentTitle}_${month}_${invoiceNumber || 'NoNumber'}.pdf`;
  doc.save(filename);
};
